import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  TruckIcon, 
  MapPinIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  PrinterIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  BellIcon,
  CalendarIcon,
  CubeIcon,
  ScaleIcon,
  ArrowsPointingOutIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UserIcon,
  BuildingOfficeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Barcode from 'react-barcode';

function Track() {
  const location = useLocation();
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [email, setEmail] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const number = params.get('number');
    if (number) {
      setTrackingNumber(number);
      handleTrack(number);
    }
  }, [location]);

  const handleTrack = async (number = trackingNumber) => {
    if (!number.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const shipmentsRef = collection(db, 'shipments');
      const q = query(shipmentsRef, where('trackingNumber', '==', number));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('No shipment found with this tracking number');
        setShipmentData(null);
        return;
      }

      const shipment = querySnapshot.docs[0].data();
      const shipmentId = querySnapshot.docs[0].id;

      // Format the shipment data for display
      const formattedData = {
        id: shipmentId,
        trackingNumber: shipment.trackingNumber,
        status: shipment.status,
        currentLocation: shipment.currentLocation || 'Processing',
        estimatedDelivery: shipment.estimatedDelivery || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        timeline: shipment.timeline || [
          {
            location: shipment.currentLocation || 'Processing',
            status: shipment.status,
            timestamp: shipment.createdAt?.toDate().toISOString() || new Date().toISOString(),
            description: 'Shipment created'
          }
        ],
        sender: {
          name: shipment.senderName || 'Not specified',
          email: shipment.senderEmail || 'Not specified',
          phone: shipment.senderPhone || 'Not specified',
          address: `${shipment.senderAddress || 'Not specified'}, ${shipment.senderCity || ''}, ${shipment.senderStateProvince || shipment.senderProvince || ''} ${shipment.senderPostalCode || ''}, ${shipment.senderCountry || ''}`
        },
        receiver: {
          name: shipment.recipientName,
          email: shipment.recipientEmail,
          phone: shipment.recipientPhone,
          address: `${shipment.recipientAddress}, ${shipment.recipientCity}, ${shipment.recipientStateProvince || shipment.recipientProvince || ''} ${shipment.recipientPostalCode || ''}, ${shipment.recipientCountry || ''}`
        },
        service: shipment.packageType === 'standard' ? 'Standard Delivery' : 
                shipment.packageType === 'express' ? 'Express Delivery' : 'Priority Delivery',
        weight: `${shipment.weight} kg`,
        dimensions: shipment.dimensions ? 
          `${shipment.dimensions.length}×${shipment.dimensions.width}×${shipment.dimensions.height} cm` : 
          'Not specified',
        value: shipment.value ? `$${shipment.value}` : 'Not specified',
        insurance: shipment.insurance || 'Standard Coverage',
        specialInstructions: shipment.notes || 'No special instructions'
      };

      setShipmentData(formattedData);
    } catch (err) {
      console.error('Error fetching shipment:', err);
      setError('Failed to fetch tracking information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'text-green-500';
      case 'in_transit':
        return 'text-blue-500';
      case 'exception':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const handlePrint = () => {
    setShowPrintPreview(true);
    setTimeout(() => {
      window.print();
      setShowPrintPreview(false);
    }, 100);
  };

  const handleCopyTracking = () => {
    navigator.clipboard.writeText(trackingNumber);
    // You could add a toast notification here
  };

  const handleGetUpdates = () => {
    setShowUpdateModal(true);
    setUpdateSuccess(false);
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setUpdateSuccess(true);
    setIsSubmitting(false);
    
    setTimeout(() => {
      setShowUpdateModal(false);
      setEmail('');
      setUpdateSuccess(false);
    }, 2000);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Track My Package',
      text: `Track my package with tracking number: ${trackingNumber}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support the Web Share API
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-24 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Search Section */}
        <div className="bg-white rounded-2xl p-8 shadow-2xl mb-8">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-gray-900 mb-3">Track Your Shipment</h1>
            <p className="text-lg text-gray-600">Enter your tracking number to get real-time updates</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                placeholder="Enter tracking number"
                className="w-full px-6 py-4 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-white text-lg font-medium transition-all"
              />
            </div>
            <button
              onClick={() => handleTrack()}
              disabled={loading}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-orange-500 text-white rounded-xl hover:shadow-xl transition-all duration-300 font-bold flex items-center justify-center gap-2 disabled:opacity-50 text-lg transform hover:scale-105"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="w-6 h-6 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <TruckIcon className="w-6 h-6" />
                  <span>Track</span>
                </>
              )}
            </button>
          </div>
          {error && (
            <p className="mt-4 text-red-600 flex items-center gap-2 text-lg font-medium bg-red-50 p-4 rounded-xl">
              <ExclamationCircleIcon className="w-6 h-6" />
              {error}
            </p>
          )}
        </div>

        {/* Tracking Results */}
        {shipmentData && (
          <div className="space-y-8" ref={printRef}>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handlePrint}
                className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center gap-2 text-lg font-semibold shadow-lg hover:shadow-xl border-2 border-gray-200"
              >
                <PrinterIcon className="w-5 h-5" />
                <span>Print</span>
              </button>
              <button
                onClick={handleCopyTracking}
                className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center gap-2 text-lg font-semibold shadow-lg hover:shadow-xl border-2 border-gray-200"
              >
                <DocumentDuplicateIcon className="w-5 h-5" />
                <span>Copy</span>
              </button>
              <button 
                onClick={handleGetUpdates}
                className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center gap-2 text-lg font-semibold shadow-lg hover:shadow-xl border-2 border-gray-200"
              >
                <BellIcon className="w-5 h-5" />
                <span>Get Updates</span>
              </button>
              <button 
                onClick={handleShare}
                className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center gap-2 text-lg font-semibold shadow-lg hover:shadow-xl border-2 border-gray-200"
              >
                <ShareIcon className="w-5 h-5" />
                <span>Share</span>
              </button>
            </div>

            {/* Status Card */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Tracking Number: {shipmentData.trackingNumber}</h2>
                  <p className="text-lg text-gray-600">{shipmentData.service}</p>
                </div>
                <div className={`text-right ${getStatusColor(shipmentData.status)}`}>
                  <p className="text-3xl font-black mb-2 capitalize">{shipmentData.status.replace(/_/g, ' ')}</p>
                  <p className="text-lg text-gray-600">Current Location: {shipmentData.currentLocation}</p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPinIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">From</p>
                    <p className="text-gray-900 font-semibold">{shipmentData.sender.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <MapPinIcon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">To</p>
                    <p className="text-gray-900 font-semibold">{shipmentData.receiver.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Barcode Section */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="flex flex-col items-center">
                <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200 w-full max-w-md">
                  {/* Label Header */}
                  <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-300">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg flex items-center justify-center">
                        <TruckIcon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-900 font-bold">CargoHaul</span>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600 text-sm">Tracking Number</p>
                      <p className="text-gray-800 font-mono font-medium">{shipmentData.trackingNumber}</p>
                    </div>
                  </div>

                  {/* Barcode */}
                  <div className="mb-4">
                    <Barcode 
                      value={shipmentData.trackingNumber}
                      width={1.5}
                      height={60}
                      fontSize={0}
                      margin={0}
                      displayValue={false}
                    />
                  </div>

                  {/* Label Details */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">From</p>
                      <p className="text-gray-800 font-medium">{shipmentData.sender.name}</p>
                      <p className="text-gray-600">{shipmentData.sender.address}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">To</p>
                      <p className="text-gray-800 font-medium">{shipmentData.receiver.name}</p>
                      <p className="text-gray-600">{shipmentData.receiver.address}</p>
                    </div>
                  </div>

                  {/* Package Info */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-1">Service</p>
                        <p className="text-gray-800 font-medium">{shipmentData.service}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Weight</p>
                        <p className="text-gray-800 font-medium">{shipmentData.weight}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Status</p>
                        <p className="text-gray-800 font-medium capitalize">{shipmentData.status.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Label Footer */}
                  <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                    <p className="text-gray-600 text-xs">Scan this barcode to track your shipment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Package Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <CubeIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Package Type</p>
                      <p className="text-xl text-gray-900 font-semibold">{shipmentData.service}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 bg-orange-100 rounded-xl">
                      <ScaleIcon className="w-8 h-8 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Weight</p>
                      <p className="text-xl text-gray-900 font-semibold">{shipmentData.weight}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 bg-purple-100 rounded-xl">
                      <ArrowsPointingOutIcon className="w-8 h-8 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Dimensions</p>
                      <p className="text-xl text-gray-900 font-semibold">{shipmentData.dimensions}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 bg-green-100 rounded-xl">
                      <CurrencyDollarIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Declared Value</p>
                      <p className="text-xl text-gray-900 font-semibold">{shipmentData.value}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 bg-indigo-100 rounded-xl">
                      <ShieldCheckIcon className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Insurance</p>
                      <p className="text-xl text-gray-900 font-semibold">{shipmentData.insurance}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="p-3 bg-yellow-100 rounded-xl">
                      <CalendarIcon className="w-8 h-8 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Estimated Delivery</p>
                      <p className="text-xl text-gray-900 font-semibold">{formatDate(shipmentData.estimatedDelivery)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Tracking Timeline</h3>
              <div className="space-y-8">
                {shipmentData.timeline && shipmentData.timeline
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((event, index) => (
                  <div key={index} className="flex gap-6">
                    <div className="flex flex-col items-center">
                      <div className={`w-5 h-5 rounded-full border-2 ${
                        index === 0 
                          ? 'bg-gradient-to-r from-blue-600 to-orange-500 border-blue-600' 
                          : 'bg-gray-300 border-gray-400'
                      }`} />
                      {index !== shipmentData.timeline.length - 1 && (
                        <div className="w-0.5 h-full bg-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xl font-bold text-gray-900 capitalize">{event.status.replace(/_/g, ' ')}</h4>
                        <span className="text-gray-600 text-sm font-medium">{formatDate(event.timestamp)}</span>
                      </div>
                      <p className="text-gray-900 font-semibold mb-1">{event.location}</p>
                      <p className="text-gray-600">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-gray-50 rounded-xl">
                  <h4 className="text-xl text-gray-900 mb-4 flex items-center gap-3 font-bold">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <UserIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    Sender
                  </h4>
                  <div className="space-y-2">
                    <p className="text-lg text-gray-900 font-semibold">{shipmentData.sender.name}</p>
                    <p className="text-gray-600">{shipmentData.sender.email}</p>
                    <p className="text-gray-600">{shipmentData.sender.phone}</p>
                    <p className="text-gray-600">{shipmentData.sender.address}</p>
                  </div>
                </div>
                <div className="p-6 bg-gray-50 rounded-xl">
                  <h4 className="text-xl text-gray-900 mb-4 flex items-center gap-3 font-bold">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <BuildingOfficeIcon className="w-5 h-5 text-orange-600" />
                    </div>
                    Receiver
                  </h4>
                  <div className="space-y-2">
                    <p className="text-lg text-gray-900 font-semibold">{shipmentData.receiver.name}</p>
                    <p className="text-gray-600">{shipmentData.receiver.email}</p>
                    <p className="text-gray-600">{shipmentData.receiver.phone}</p>
                    <p className="text-gray-600">{shipmentData.receiver.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            {shipmentData.specialInstructions && (
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Special Instructions</h3>
                <p className="text-lg text-gray-700 leading-relaxed">{shipmentData.specialInstructions}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Get Package Updates</h3>
              <button 
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {!updateSuccess && !isSubmitting ? (
              <form onSubmit={handleSubmitEmail} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-gray-700 mb-2 text-lg font-medium">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-white text-lg transition-all"
                    placeholder="Enter your email"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-orange-500 text-white rounded-xl hover:shadow-xl transition-all duration-300 text-lg font-bold"
                >
                  Subscribe to Updates
                </button>
              </form>
            ) : isSubmitting ? (
              <div className="text-center py-8">
                <div className="flex flex-col items-center gap-4">
                  <ArrowPathIcon className="w-12 h-12 text-blue-600 animate-spin" />
                  <p className="text-gray-700 text-lg font-medium">Subscribing to updates...</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-900 text-xl font-semibold">You'll receive updates at {email}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #print-content, #print-content * {
              visibility: visible;
            }
            #print-content {
              position: absolute;
              left: 0;
              top: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Track; 